import type { FormEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { TFunction } from 'i18next';
import { ChevronDownIcon, EllipsisIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
import { Dropdown } from '../Dropdown';
import {
  ConnectWalletIcon,
  GlobeIcon,
  LoginFeatureIcon,
  OldUserArrowIcon,
  OldUserMethodIcon,
  RefreshGradientIcon,
  WalletGradientIcon,
} from '../icons/LoginIcons';
import {
  AVAILABLE_LANGUAGES,
  COLORS,
  HERO_IMAGE,
  LOGIN_FEATURES,
  OLD_USER_METHODS,
  SUPPORTED_WALLETS,
} from '../../constants';

const WALLET_ICON_CLASS = 'size-8';

export type LineOption = {
  id: string;
  label: string;
};

type LoginT = TFunction<'translation', undefined>;

export function TopBar({
  t,
  isZh,
  lines,
  currentLine,
  selectedLine,
  showLineDropdown,
  setSelectedLine,
  setShowLineDropdown,
  currentLanguage,
  showLangDropdown,
  setShowLangDropdown,
  onLanguageChange,
}: {
  t: LoginT;
  isZh: boolean;
  lines: LineOption[];
  currentLine?: LineOption;
  selectedLine: string;
  showLineDropdown: boolean;
  setSelectedLine: (line: string) => void;
  setShowLineDropdown: (open: boolean) => void;
  currentLanguage: (typeof AVAILABLE_LANGUAGES)[number];
  showLangDropdown: boolean;
  setShowLangDropdown: (open: boolean) => void;
  onLanguageChange: (lang: string) => void;
}) {
  const titleClass = isZh
    ? 'justify-self-center text-center text-xs text-white/90 tracking-[0.84px] whitespace-nowrap'
    : 'justify-self-center max-w-[100px] truncate text-center text-xs leading-tight text-white/90 tracking-[0.84px]';

  return (
    <div
      className="fixed left-1/2 top-0 z-50 grid w-full max-w-[430px] -translate-x-1/2 grid-cols-[1fr_auto_1fr] items-center gap-2 px-4 pb-3 pt-[35px]"
      style={{ background: COLORS.topbarBg }}
    >
      <div className="flex min-w-0 justify-start">
        <Dropdown
          isOpen={showLineDropdown}
          onClose={() => setShowLineDropdown(false)}
          items={lines}
          selectedValue={selectedLine}
          getItemKey={(line) => line.id}
          onSelect={(line) => setSelectedLine(line.id)}
          align="left"
          width="120px"
          borderColor={COLORS.bgDropdown}
          background={COLORS.bgDropdown}
          renderTrigger={(isOpen) => (
            <button
              type="button"
              onClick={() => setShowLineDropdown(!showLineDropdown)}
              className="flex max-w-full items-center gap-2 rounded-full border-[0.5px] bg-transparent px-2.5 py-1.5 text-xs text-white/90 transition-colors hover:bg-white/5"
              style={{ borderColor: COLORS.borderDropdown }}
            >
              <span className="truncate">{currentLine ? currentLine.label : t('auth.switchLine')}</span>
              <DropdownChevron isOpen={isOpen} />
            </button>
          )}
          renderItem={(line, isSelected) => (
            <span className={isSelected ? '' : 'text-white/90'} style={isSelected ? { color: COLORS.selectedText } : undefined}>
              {line.label}
            </span>
          )}
        />
      </div>

      <div className={titleClass}>I. {t('auth.consensus')}</div>

      <div className="flex min-w-0 justify-end">
        <Dropdown
          isOpen={showLangDropdown}
          onClose={() => setShowLangDropdown(false)}
          items={AVAILABLE_LANGUAGES}
          selectedValue={currentLanguage.code}
          getItemKey={(lang) => lang.code}
          onSelect={(lang) => onLanguageChange(lang.code)}
          align="right"
          width="140px"
          borderColor={COLORS.borderDropdown}
          background={COLORS.bgLanguageMenu}
          renderTrigger={(isOpen) => (
            <button
              type="button"
              onClick={() => setShowLangDropdown(!showLangDropdown)}
              className="flex max-w-full items-center gap-1.5 rounded-full border-[0.5px] px-2.5 py-1.5 text-xs transition-all hover:brightness-110"
              style={{ borderColor: COLORS.bgDropdown, background: COLORS.bgDropdown }}
            >
              <GlobeIcon />
              <span className="truncate text-white/90">{currentLanguage.label}</span>
              <DropdownChevron isOpen={isOpen} />
            </button>
          )}
          renderItem={(lang, isSelected) => (
            <span className={isSelected ? '' : 'text-white/90'} style={isSelected ? { color: COLORS.selectedText } : undefined}>
              {lang.label}
            </span>
          )}
        />
      </div>
    </div>
  );
}

export function HeroSection({ t }: { t: LoginT }) {
  return (
    <div className="relative min-h-[220px] overflow-hidden">
      <img src={HERO_IMAGE} alt={t('auth.heroImageAlt')} className="absolute inset-0 h-full w-full object-cover object-center" />
      <div className="relative z-10 flex min-h-[220px] max-w-[220px] flex-col items-start justify-center gap-2 pl-6">
        <div className="text-xs font-medium uppercase" style={{ color: COLORS.iconGlobal }}>
          {t('auth.welcomeTo')}
        </div>
        <div className="flex items-center gap-1 text-[34px] font-bold leading-none">
          <span style={{ background: COLORS.orbText, backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            ORB
          </span>
          <span style={{ color: COLORS.lightYellow, textShadow: COLORS.textShadow }}>{t('auth.orbTitle')}</span>
        </div>
        <div className="flex flex-col gap-1">
          <div className="text-sm leading-snug" style={{ color: COLORS.iconGlobal }}>{t('auth.slogan1')}</div>
          <div className="text-sm leading-snug" style={{ color: COLORS.iconGlobal }}>{t('auth.slogan2')}</div>
        </div>
      </div>
    </div>
  );
}

export function ClassicLoginCard({
  t,
  username,
  password,
  rememberMe,
  agreedTerms,
  showPassword,
  loading,
  error,
  onUsernameChange,
  onPasswordChange,
  onRememberMeChange,
  onAgreedTermsChange,
  onShowPasswordToggle,
  onSubmit,
}: {
  t: LoginT;
  username: string;
  password: string;
  rememberMe: boolean;
  agreedTerms: boolean;
  showPassword: boolean;
  loading: boolean;
  error: string | null;
  onUsernameChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onRememberMeChange: (value: boolean) => void;
  onAgreedTermsChange: (value: boolean) => void;
  onShowPasswordToggle: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}) {
  return (
    <LoginCard background={COLORS.bgWallet}>
      <div className="mb-1">
        <div className="text-2xl font-bold leading-none" style={{ color: COLORS.iconGlobal }}>{t('auth.hello')}</div>
        <div className="mt-1 text-xs" style={{ color: COLORS.iconColor }}>{t('auth.welcome')}</div>
      </div>

      <form onSubmit={onSubmit} className="space-y-3">
        <label className="block">
          <span className="mb-1.5 block text-xs font-medium" style={{ color: COLORS.iconColor }}>{t('auth.username')}</span>
          <input
            className="h-12 w-full rounded-full border bg-black/25 px-4 text-sm text-white outline-none placeholder:text-white/35 focus:ring-2 focus:ring-brand-500/25"
            style={{ borderColor: COLORS.borderColor }}
            value={username}
            onChange={(event) => onUsernameChange(event.target.value)}
            placeholder={t('auth.placeholderUsername')}
            autoCapitalize="none"
            autoCorrect="off"
            autoComplete="username"
            required
          />
        </label>

        <label className="block">
          <span className="mb-1.5 block text-xs font-medium" style={{ color: COLORS.iconColor }}>{t('auth.password')}</span>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              className="h-12 w-full rounded-full border bg-black/25 px-4 pr-12 text-sm text-white outline-none placeholder:text-white/35 focus:ring-2 focus:ring-brand-500/25"
              style={{ borderColor: COLORS.borderColor }}
              value={password}
              onChange={(event) => onPasswordChange(event.target.value)}
              placeholder={t('auth.placeholderPassword')}
              autoComplete="current-password"
              required
            />
            <button
              type="button"
              onClick={onShowPasswordToggle}
              className="absolute right-3 top-1/2 flex size-8 -translate-y-1/2 items-center justify-center text-white/50"
              aria-label={showPassword ? t('common.hide', 'Hide') : t('common.show', 'Show')}
            >
              {showPassword ? <EyeOffIcon className="size-4" /> : <EyeIcon className="size-4" />}
            </button>
          </div>
        </label>

        <div className="flex items-center justify-between gap-2 text-[11px]">
          <label className="flex cursor-pointer items-center gap-2" style={{ color: COLORS.iconColor }}>
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(event) => onRememberMeChange(event.target.checked)}
              className="size-3.5 rounded border accent-[#BFD648]"
            />
            {t('auth.rememberMe')}
          </label>
          <div className="flex items-center gap-2">
            <Link to="/forgot-password" className="hover:underline" style={{ color: COLORS.selectedText }}>
              {t('auth.forgotPassword')}
            </Link>
            <span style={{ color: COLORS.line }}>|</span>
            <Link to="/reset-security" className="hover:underline" style={{ color: COLORS.selectedText }}>
              {t('auth.resetSecurity')}
            </Link>
          </div>
        </div>

        <label className="flex cursor-pointer items-start gap-2 text-[10px] leading-snug" style={{ color: COLORS.iconColor }}>
          <input
            type="checkbox"
            checked={agreedTerms}
            onChange={(event) => onAgreedTermsChange(event.target.checked)}
            className="mt-0.5 size-3.5 shrink-0 rounded border accent-[#BFD648]"
            required
          />
          <span>
            {t('auth.agreePrefix')}{' '}
            <span style={{ color: COLORS.selectedText }}>{t('auth.userAgreement')}</span>
            {' '}{t('auth.and')}{' '}
            <span style={{ color: COLORS.selectedText }}>{t('auth.privacyPolicy')}</span>
          </span>
        </label>

        {error ? (
          <div className="rounded-lg border border-red-400/30 bg-red-500/10 px-3 py-2 text-xs text-red-200">{error}</div>
        ) : null}

        <button
          type="submit"
          disabled={loading || !agreedTerms}
          className="h-12 w-full rounded-full text-sm font-bold transition-all hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
          style={{ background: COLORS.loginBtnBg, color: COLORS.loginBtnText, boxShadow: COLORS.shadowConnect }}
        >
          {loading ? t('auth.loggingIn') : t('auth.loginBtn')}
        </button>

        <p className="pt-1 text-center text-xs" style={{ color: COLORS.iconColor }}>
          {t('auth.noAccount')}{' '}
          <Link to="/register" className="font-semibold hover:underline" style={{ color: COLORS.selectedText }}>
            {t('auth.registerBtn')}
          </Link>
        </p>
      </form>
    </LoginCard>
  );
}

export function WalletLoginCard({
  t,
  busy = false,
  error,
  onConnectWallet,
  onWalletSelect,
}: {
  t: LoginT;
  busy?: boolean;
  error?: string | null;
  onConnectWallet: () => void;
  onWalletSelect: (wallet: string) => void;
}) {
  return (
    <LoginCard background={COLORS.bgWallet}>
      <CardHeader icon={<WalletGradientIcon />} title={t('auth.walletLoginTitle')} description={t('auth.walletLoginDesc')} />

      {error ? (
        <p className="rounded-lg border px-3 py-2 text-xs" style={{ borderColor: COLORS.borderConnect, color: '#f87171' }}>
          {error}
        </p>
      ) : null}

      <button
        type="button"
        onClick={onConnectWallet}
        disabled={busy}
        className="flex h-[58px] w-full flex-col items-center justify-center gap-1 rounded-xl border px-5 text-center transition-all hover:brightness-110 disabled:opacity-60"
        style={{ background: COLORS.bgConnect, borderColor: COLORS.borderConnect, boxShadow: COLORS.shadowConnect }}
      >
        <div className="flex items-center justify-center gap-2 leading-none">
          <ConnectWalletIcon />
          <div className="text-sm font-medium" style={{ color: COLORS.iconGlobal }}>
            {busy ? t('auth.connectingWallet') : t('auth.connectWallet')}
          </div>
        </div>
        <div className="text-[10px] leading-tight" style={{ color: COLORS.iconGlobal }}>{t('auth.connectWalletDesc')}</div>
      </button>

      <div className="flex flex-col gap-3">
        <DividerLabel label={t('auth.supportedWallets')} />
        <div className="grid grid-cols-4 items-stretch gap-2">
          {SUPPORTED_WALLETS.map((wallet) => {
            const walletName = t(`auth.wallets.${wallet.id}`);

            return (
              <button
                type="button"
                key={wallet.id}
                onClick={() => onWalletSelect(walletName)}
                className="flex flex-col items-center justify-start gap-2"
              >
                <div className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-lg p-1.5" style={{ background: wallet.background }}>
                  {'iconSrc' in wallet ? (
                    <img src={wallet.iconSrc} alt={walletName} className={`${WALLET_ICON_CLASS} object-contain`} draggable={false} />
                  ) : (
                    <EllipsisIcon className={WALLET_ICON_CLASS} color={COLORS.iconGlobal} strokeWidth={2.25} />
                  )}
                </div>
                <div className="text-center text-[10px] leading-none break-words" style={{ color: COLORS.iconColor }}>{walletName}</div>
              </button>
            );
          })}
        </div>
      </div>
    </LoginCard>
  );
}

export function OldUserCard({ t, onOpenLogin }: { t: LoginT; onOpenLogin: () => void }) {
  return (
    <LoginCard background={COLORS.oldUserBg}>
      <CardHeader icon={<RefreshGradientIcon />} title={t('auth.oldUserTitle')} description={t('auth.oldUserDesc')} />

      <div className="mb-2 flex items-start justify-center">
        {OLD_USER_METHODS.map((method, index) => (
          <div key={method.id} className="contents">
            <div className="flex w-[66px] flex-col items-center gap-1">
              <IconCircle>
                <OldUserMethodIcon type={method.id} />
              </IconCircle>
              <div className="flex w-full flex-col items-center gap-1 text-center">
                <div className="text-[10px] font-semibold" style={{ color: COLORS.iconGlobal }}>{t(method.labelKey)}</div>
                <div className="text-[8px]" style={{ color: COLORS.iconColor }}>{t(method.descKey)}</div>
              </div>
            </div>
            {index < OLD_USER_METHODS.length - 1 && (
              <div className="flex h-8 w-11 items-center justify-center">
                <OldUserArrowIcon />
              </div>
            )}
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={onOpenLogin}
        className="rounded-full border px-5 py-2 text-center text-xs transition-all hover:brightness-110"
        style={{ background: COLORS.oldUserButtonBg, borderColor: COLORS.oldUserButtonBorder, color: COLORS.oldUserButtonText }}
      >
        {t('auth.updateWalletLogin')}
      </button>
    </LoginCard>
  );
}

export function OldUserLoginModal({
  t,
  open,
  username,
  password,
  loading,
  error,
  onClose,
  onUsernameChange,
  onPasswordChange,
  onSubmit,
}: {
  t: LoginT;
  open: boolean;
  username: string;
  password: string;
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onUsernameChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 px-4 py-5 backdrop-blur-sm">
      <div
        className="w-full max-w-[382px] rounded-2xl border p-5 shadow-2xl"
        style={{ background: COLORS.bg, borderColor: COLORS.borderColor }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="old-user-login-title"
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 id="old-user-login-title" className="text-base font-semibold" style={{ color: COLORS.iconGlobal }}>
              {t('auth.oldUserLoginTitle')}
            </h2>
            <p className="mt-1 text-[11px] leading-snug" style={{ color: COLORS.iconColor }}>
              {t('auth.oldUserLoginDesc')}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex size-8 shrink-0 items-center justify-center rounded-full border text-lg leading-none text-white/80"
            style={{ background: COLORS.bgOther, borderColor: COLORS.borderColor }}
            aria-label={t('common.close')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M14.9073 2.84663C15.256 2.58792 15.7513 2.61637 16.0675 2.93256C16.4153 3.28037 16.4153 3.84452 16.0675 4.19233L10.7599 9.49995L16.0675 14.8076C16.4153 15.1554 16.4153 15.7195 16.0675 16.0673C15.7513 16.3835 15.256 16.412 14.9073 16.1533L14.8077 16.0673L9.50012 10.7597L4.1925 16.0673L4.09289 16.1533C3.74423 16.412 3.24892 16.3835 2.93273 16.0673C2.58492 15.7195 2.58492 15.1554 2.93273 14.8076L8.24035 9.49995L2.93273 4.19233C2.58492 3.84452 2.58492 3.28037 2.93273 2.93256C3.24892 2.61637 3.74423 2.58792 4.09289 2.84663L4.1925 2.93256L9.50012 8.24018L14.8077 2.93256L14.9073 2.84663Z"
                fill={COLORS.iconGlobal}
              />
            </svg>
          </button>
        </div>

        <form onSubmit={onSubmit} className="space-y-3">
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium" style={{ color: COLORS.iconColor }}>{t('auth.username')}</span>
            <input
              className="h-12 w-full rounded-full border bg-black/25 px-4 text-sm text-white outline-none placeholder:text-white/35 focus:ring-2 focus:ring-brand-500/25"
              style={{ borderColor: COLORS.borderColor }}
              value={username}
              onChange={(event) => onUsernameChange(event.target.value)}
              placeholder={t('auth.placeholderUsername')}
              autoCapitalize="none"
              autoCorrect="off"
              autoComplete="username"
              required
            />
          </label>

          <label className="block">
            <span className="mb-1.5 block text-xs font-medium" style={{ color: COLORS.iconColor }}>{t('auth.password')}</span>
            <input
              type="password"
              className="h-12 w-full rounded-full border bg-black/25 px-4 text-sm text-white outline-none placeholder:text-white/35 focus:ring-2 focus:ring-brand-500/25"
              style={{ borderColor: COLORS.borderColor }}
              value={password}
              onChange={(event) => onPasswordChange(event.target.value)}
              placeholder={t('auth.placeholderPassword')}
              autoComplete="current-password"
              required
            />
          </label>

          {error && <div className="rounded-lg border border-red-400/30 bg-red-500/10 px-3 py-2 text-xs text-red-200">{error}</div>}

          <button
            type="submit"
            disabled={loading}
            className="h-11 w-full rounded-full border text-sm font-semibold transition-all hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
            style={{ background: COLORS.oldUserButtonBg, borderColor: COLORS.oldUserButtonBorder, color: COLORS.oldUserButtonText }}
          >
            {loading ? t('auth.loggingIn') : t('auth.loginBtn')}
          </button>
        </form>
      </div>
    </div>
  );
}

export function FeatureBar({ t }: { t: LoginT }) {
  return (
    <div className="grid grid-cols-3 items-center gap-3 rounded-xl py-3" style={{ background: COLORS.bottomFeatureBg }}>
      {LOGIN_FEATURES.map((feature) => (
        <div key={feature.id} className="flex shrink-0 items-center justify-center gap-1">
          <LoginFeatureIcon type={feature.id} />
          <span className="whitespace-nowrap text-xs" style={{ color: COLORS.iconColor }}>{t(feature.labelKey)}</span>
        </div>
      ))}
    </div>
  );
}

function DropdownChevron({ isOpen }: { isOpen: boolean }) {
  return <ChevronDownIcon className={`size-2.5 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} strokeWidth={3} aria-hidden="true" />;
}

function LoginCard({ background, children }: { background: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-3 rounded-xl border p-4" style={{ borderColor: COLORS.borderColor, background }}>
      {children}
    </div>
  );
}

function CardHeader({ icon, title, description }: { icon: ReactNode; title: string; description: string }) {
  return (
    <div className="flex gap-3">
      <IconCircle sizeClass="size-6 p-1">{icon}</IconCircle>
      <div className="flex flex-1 flex-col gap-0.5">
        <div className="text-sm font-semibold" style={{ color: COLORS.iconGlobal }}>{title}</div>
        <div className="text-[10px] leading-snug" style={{ color: COLORS.iconColor }}>{description}</div>
      </div>
    </div>
  );
}

function IconCircle({ children, sizeClass = 'size-8 p-0.5' }: { children: ReactNode; sizeClass?: string }) {
  return (
    <div className={`${sizeClass} flex shrink-0 items-center justify-center rounded-full border`} style={{ background: COLORS.bgOther, borderColor: COLORS.borderColor }}>
      {children}
    </div>
  );
}

function DividerLabel({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2">
      <div className="h-px flex-1" style={{ background: COLORS.line }} />
      <span className="text-xs" style={{ color: COLORS.iconColor }}>{label}</span>
      <div className="h-px flex-1" style={{ background: COLORS.line }} />
    </div>
  );
}
